import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCurrency } from "@/utils/financeCalculations";
import { TrendingUp, TrendingDown, Target, Percent, Scale, Heart, AlertCircle, ArrowUp, ArrowDown, Minus } from "lucide-react";
import { format, subMonths } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useFinance } from "@/context/FinanceContext";

function useFinanceSafe() {
  try {
    const context = useFinance();
    return context;
  } catch (e) {
    console.error("[FinancialHealthCard] Failed to access FinanceContext:", e);
    return null;
  }
}

export function calculateHealthScore(margin: number): number {
  if (margin > 20) return Math.min(10, 8 + ((margin - 20) / 20) * 2);
  if (margin >= 10) return 6 + (margin - 10) / 10;
  if (margin >= 0) return 4 + margin / 10;
  return Math.max(1, 3 + margin / 30);
}

function getScoreColorClasses(score: number): { bg: string; text: string } {
  if (score >= 8) return { bg: "bg-emerald-100 dark:bg-emerald-900/30", text: "text-emerald-700 dark:text-emerald-400" };
  if (score >= 6) return { bg: "bg-amber-100 dark:bg-amber-900/30", text: "text-amber-700 dark:text-amber-400" };
  if (score >= 4) return { bg: "bg-orange-100 dark:bg-orange-900/30", text: "text-orange-700 dark:text-orange-400" };
  return { bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-400" };
}

function calcVariation(current: number, previous: number): { pct: number; direction: "up" | "down" | "neutral" } {
  if (previous === 0 && current === 0) return { pct: 0, direction: "neutral" };
  if (previous === 0) return { pct: 100, direction: "up" };
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  if (Math.abs(pct) < 0.1) return { pct: 0, direction: "neutral" };
  return { pct, direction: pct > 0 ? "up" : "down" };
}

function VariationBadge({ current, previous, invertColor = false }: { current: number; previous: number; invertColor?: boolean }) {
  const { pct, direction } = calcVariation(current, previous);

  if (direction === "neutral") {
    return (
      <div className="flex flex-col items-center mt-1">
        <span className="inline-flex items-center gap-0.5 text-sm text-muted-foreground font-medium">
          <Minus className="w-3 h-3" /> 0%
        </span>
        <span className="text-[11px] text-muted-foreground/70">vs mês anterior</span>
      </div>
    );
  }

  const isPositive = invertColor ? direction === "down" : direction === "up";

  return (
    <div className="flex flex-col items-center mt-1">
      <span className={`inline-flex items-center gap-0.5 text-sm font-semibold ${
        isPositive 
          ? "text-emerald-600 dark:text-emerald-400" 
          : "text-red-600 dark:text-red-400"
      }`}>
        {direction === "up" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
        {Math.abs(pct).toFixed(1)}%
      </span>
      <span className="text-[11px] text-muted-foreground/70">vs mês anterior</span>
    </div>
  );
}

function VariationBadgePoints({ current, previous }: { current: number; previous: number }) {
  const diff = current - previous;
  if (Math.abs(diff) < 0.05) {
    return (
      <div className="flex flex-col items-center mt-1">
        <span className="inline-flex items-center gap-0.5 text-sm text-muted-foreground font-medium">
          <Minus className="w-3 h-3" /> 0 pts
        </span>
        <span className="text-[11px] text-muted-foreground/70">vs mês anterior</span>
      </div>
    );
  }
  const isPositive = diff > 0;
  return (
    <div className="flex flex-col items-center mt-1">
      <span className={`inline-flex items-center gap-0.5 text-sm font-semibold ${
        isPositive 
          ? "text-emerald-600 dark:text-emerald-400" 
          : "text-red-600 dark:text-red-400"
      }`}>
        {isPositive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
        {Math.abs(diff).toFixed(1)} pts
      </span>
      <span className="text-[11px] text-muted-foreground/70">vs mês anterior</span>
    </div>
  );
}

function VariationBadgeMargin({ current, previous }: { current: number; previous: number }) {
  const diff = current - previous;
  if (Math.abs(diff) < 0.05) {
    return (
      <div className="flex flex-col items-center mt-1">
        <span className="inline-flex items-center gap-0.5 text-sm text-muted-foreground font-medium">
          <Minus className="w-3 h-3" /> 0 p.p.
        </span>
        <span className="text-[11px] text-muted-foreground/70">vs mês anterior</span>
      </div>
    );
  }
  const isPositive = diff > 0;
  return (
    <div className="flex flex-col items-center mt-1">
      <span className={`inline-flex items-center gap-0.5 text-sm font-semibold ${
        isPositive 
          ? "text-emerald-600 dark:text-emerald-400" 
          : "text-red-600 dark:text-red-400"
      }`}>
        {isPositive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
        {Math.abs(diff).toFixed(1)} p.p.
      </span>
      <span className="text-[11px] text-muted-foreground/70">vs mês anterior</span>
    </div>
  );
}

export function FinancialHealthCard() {
  const financeData = useFinanceSafe();
  
  if (!financeData) {
    return (
      <Card className="w-full">
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <AlertCircle className="w-5 h-5" />
            <span>Carregando dados financeiros...</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const { transactions, referenceDate } = financeData;

  const { current, previous } = useMemo(() => {
    function calcMonth(month: number, year: number) {
      const monthTx = transactions.filter((t) => {
        if (!t.dueDate) return false;
        const date = new Date(t.dueDate);
        return date.getMonth() === month && date.getFullYear() === year;
      });
      const income = monthTx.filter((t) => t.type === "receita").reduce((s, t) => s + t.value, 0);
      const expenses = monthTx.filter((t) => t.type === "despesa").reduce((s, t) => s + t.value, 0);
      const profit = income - expenses;
      const margin = income > 0 ? (profit / income) * 100 : 0;
      const healthScore = calculateHealthScore(margin);
      return { monthlyIncome: income, monthlyExpenses: expenses, profit, margin, breakEven: expenses, healthScore };
    }

    const cur = calcMonth(referenceDate.getMonth(), referenceDate.getFullYear());
    const prevDate = subMonths(referenceDate, 1);
    const prev = calcMonth(prevDate.getMonth(), prevDate.getFullYear());
    return { current: cur, previous: prev };
  }, [transactions, referenceDate]);

  const scoreColors = getScoreColorClasses(current.healthScore);
  const monthName = format(referenceDate, "MMMM 'de' yyyy", { locale: ptBR });

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <Heart className="w-5 h-5 text-primary" />
          Saúde Financeira — {monthName.charAt(0).toUpperCase() + monthName.slice(1)}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {/* Entradas */}
          <div className="text-center p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
            <div className="flex items-center justify-center gap-1 mb-1">
              <TrendingUp className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <p className="text-sm text-muted-foreground">Entradas</p>
            </div>
            <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
              {formatCurrency(current.monthlyIncome, false)}
            </p>
            <VariationBadge current={current.monthlyIncome} previous={previous.monthlyIncome} />
          </div>

          {/* Saídas */}
          <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
            <div className="flex items-center justify-center gap-1 mb-1">
              <TrendingDown className="w-4 h-4 text-red-600 dark:text-red-400" />
              <p className="text-sm text-muted-foreground">Saídas</p>
            </div>
            <p className="text-lg font-bold text-red-600 dark:text-red-400">
              {formatCurrency(current.monthlyExpenses, false)}
            </p>
            <VariationBadge current={current.monthlyExpenses} previous={previous.monthlyExpenses} invertColor />
          </div>

          {/* Lucro */}
          <div className={`text-center p-4 rounded-lg border ${
            current.profit >= 0 
              ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800" 
              : "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
          }`}>
            <div className="flex items-center justify-center gap-1 mb-1">
              <Scale className="w-4 h-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Lucro</p>
            </div>
            <p className={`text-lg font-bold ${
              current.profit >= 0 
                ? "text-emerald-600 dark:text-emerald-400" 
                : "text-red-600 dark:text-red-400"
            }`}>
              {formatCurrency(current.profit, true)}
            </p>
            <VariationBadge current={current.profit} previous={previous.profit} />
          </div>

          {/* Margem */}
          <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Percent className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <p className="text-sm text-muted-foreground">Margem de Lucro</p>
            </div>
            <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
              {current.margin.toFixed(1)}%
            </p>
            <VariationBadgeMargin current={current.margin} previous={previous.margin} />
          </div>

          {/* Ponto de Equilíbrio */}
          <div className="text-center p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Target className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              <p className="text-sm text-muted-foreground">Ponto de Equilíbrio</p>
            </div>
            <p className="text-lg font-bold text-amber-600 dark:text-amber-400">
              {formatCurrency(current.breakEven, false)}
            </p>
            <VariationBadge current={current.breakEven} previous={previous.breakEven} invertColor />
          </div>

          {/* Nota */}
          <div className={`text-center p-4 rounded-lg border ${scoreColors.bg} border-current/20`}>
            <div className="flex items-center justify-center gap-1 mb-1">
              <Heart className={`w-4 h-4 ${scoreColors.text}`} />
              <p className="text-sm text-muted-foreground">Nota</p>
            </div>
            <p className={`text-2xl font-bold ${scoreColors.text}`}>
              {current.healthScore.toFixed(1)}
            </p>
            <VariationBadgePoints current={current.healthScore} previous={previous.healthScore} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
